import { Link } from 'react-router-dom';
import { Leaf, Sun, Droplets, ArrowRight, Plus } from 'lucide-react';
import { storeApi } from '../../lib/api';
import { useCart, formatAUD } from './CartContext';
import { toast } from 'sonner';

export const LEVELS = { low: 1, medium: 2, high: 3 };

export function ToleranceBar({ level, testId }) {
  const n = LEVELS[level] || 0;
  return (
    <div className="store-tolerance" data-testid={testId}>
      {[1, 2, 3].map((i) => <span key={i} className={i <= n ? 'on' : ''} />)}
    </div>
  );
}

export function VarietyPhoto({ variety, className = '' }) {
  const photo = variety.photos?.[0];
  if (photo) {
    return <img src={storeApi.photoUrl(photo)} alt={variety.name} className={`object-cover ${className}`} loading="lazy" />;
  }
  return (
    <div className={`store-placeholder flex items-center justify-center ${className}`}>
      <Leaf className="h-12 w-12 text-white/70" />
    </div>
  );
}

export function VarietyCard({ variety, index = 0 }) {
  const { addItem } = useCart();

  const quickAdd = (e) => {
    e.preventDefault();
    addItem(variety, variety.min_order_m2 || 10);
    toast.success(`${variety.name} added to your order`);
  };

  return (
    <Link to={`/shop/varieties/${variety.slug}`} className={`store-card flex flex-col store-rise store-rise-${Math.min(index % 3 + 1, 3)}`} data-testid={`variety-card-${variety.slug}`}>
      <div className="relative aspect-[4/3] overflow-hidden">
        <VarietyPhoto variety={variety} className="w-full h-full" />
        {!variety.in_stock && (
          <span className="absolute top-3 left-3 bg-white/90 text-xs font-semibold px-3 py-1 rounded-full" style={{ color: 'var(--s-terra)' }}>Currently unavailable</span>
        )}
        <span className="absolute bottom-3 right-3 bg-white rounded-full px-3 py-1.5 text-sm font-heading font-bold shadow-sm" style={{ color: 'var(--s-green)' }} data-testid={`variety-price-${variety.slug}`}>
          {formatAUD(variety.price_per_m2)}<span className="text-xs font-medium text-neutral-500">/m²</span>
        </span>
      </div>
      <div className="p-5 flex flex-col gap-3 flex-1">
        <div>
          <h3 className="font-heading font-bold text-lg leading-snug" style={{ color: 'var(--s-ink)' }}>{variety.name}</h3>
          {variety.ideal_for && <p className="text-sm mt-1" style={{ color: 'var(--s-muted)' }}>{variety.ideal_for}</p>}
        </div>
        <div className="grid grid-cols-3 gap-3 text-[11px] uppercase tracking-wider font-semibold" style={{ color: 'var(--s-muted)' }}>
          <div><span className="flex items-center gap-1 mb-1"><Sun className="h-3 w-3" /> Shade</span><ToleranceBar level={variety.shade_tolerance} /></div>
          <div><span className="flex items-center gap-1 mb-1"><Droplets className="h-3 w-3" /> Drought</span><ToleranceBar level={variety.drought_tolerance} /></div>
          <div><span className="flex items-center gap-1 mb-1"><Leaf className="h-3 w-3" /> Wear</span><ToleranceBar level={variety.wear_tolerance} /></div>
        </div>
        <div className="mt-auto pt-2 flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: 'var(--s-green)' }}>View details <ArrowRight className="h-4 w-4" /></span>
          {variety.in_stock && (
            <button onClick={quickAdd} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 text-white transition-transform hover:scale-105" style={{ background: 'var(--s-green)' }} data-testid={`quick-add-${variety.slug}`}>
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          )}
        </div>
      </div>
    </Link>
  );
}
